"use client"
import type { Post } from '@/lib/api'
import Link from 'next/link'
import Image from 'next/image'
import { useLocale, useTranslations } from 'next-intl'
import { getCategoryColor } from '@/lib/constants'

export default function PopularList({ posts }: { posts: Post[] }) {
  const t = useTranslations('ui')
  const locale = useLocale()
  
  if (!posts.length) return null
  
  return (
    <aside className="overflow-hidden rounded-xl bg-white shadow-sm">
      {/* Header avec accent */}
      <div className="border-b bg-gradient-to-r from-blue-600 to-blue-700 p-4">
        <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-white">
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
          {t('popular')}
        </h3>
      </div>
      
      {/* Liste numérotée */}
      <div className="p-4">
        <ol className="space-y-4">
          {posts.map((post, index) => {
            const colors = getCategoryColor(post.category)
            return (
              <li key={post.id} className="group flex gap-3">
                {/* Numéro */}
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-sm font-bold text-gray-600">
                  {index + 1}
                </span>
                
                {/* Contenu */}
                <div className="flex-1">
                  <Link 
                    prefetch 
                    href={`/${locale}/${post.category}/${post.slug}`} 
                    className="block"
                  >
                    {/* Catégorie */}
                    <span className={`mb-1 inline-block text-[10px] font-bold uppercase tracking-wider ${colors.text}`}>
                      {post.category}
                    </span>
                    
                    {/* Titre */}
                    <h4 className="line-clamp-2 text-sm font-semibold leading-tight text-gray-900 transition-colors group-hover:text-blue-600">
                      {post.title}
                    </h4>
                    
                    {/* Date */}
                    <time className="mt-1 text-xs text-gray-500">
                      {new Date(post.publishedAt).toLocaleDateString(locale)}
                    </time>
                  </Link>
                </div>
                
                {/* Thumbnail pour tous les articles */}
                {post.coverImage && (
                  <Link 
                    prefetch 
                    href={`/${locale}/${post.category}/${post.slug}`}
                    className="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg"
                  >
                    <Image
                      src={post.coverImage}
                      alt={post.title}
                      fill
                      className="object-cover transition-transform group-hover:scale-110"
                      sizes="64px"
                    />
                    {/* Badge vidéo si c'est une vidéo */}
                    {post.type === 'video' && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-600/90 text-white">
                          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        </div>
                      </div>
                    )}
                  </Link>
                )}
              </li>
            )
          })}
        </ol>
      </div>
    </aside>
  )
}

